import {
    apiRefreshTokenStorage,
    apiTokenStorage,
    authenticatedStorage,
    tenantStorage,
} from '@/helpers/authStorage'
import {
    AUTH_AUTHENTICATE_USER_FAILURE,
    AUTH_AUTHENTICATE_USER_REQUEST,
    AUTH_AUTHENTICATE_USER_SUCCESS,
    AUTH_FORGOT_PASSWORD_FAILURE,
    AUTH_FORGOT_PASSWORD_REQUEST,
    AUTH_FORGOT_PASSWORD_SUCCESS,
    AUTH_SET_API_TOKEN,
    AUTH_SET_REFRESH_TOKEN,
    AUTH_SET_TENANT,
    AUTH_SUBMIT_PASSWORD_FAILURE,
    AUTH_SUBMIT_PASSWORD_REQUEST,
    AUTH_SUBMIT_PASSWORD_SUCCESS,
    AUTH_UNSET_TOKENS,
} from '@/redux/actionTypes'

export interface AuthenticationState {
    loading: boolean
    authenticateFailure: boolean
    newPasswordSuccess: boolean
    isAuthenticated: boolean
    tenant: string | null
    apiToken: string | null
    apiRefreshToken: string | null
    errors: unknown[]
}

export type AuthenticationAction =
    | { type: typeof AUTH_AUTHENTICATE_USER_REQUEST }
    | { type: typeof AUTH_AUTHENTICATE_USER_SUCCESS, apiToken: string, refreshToken: string }
    | { type: typeof AUTH_AUTHENTICATE_USER_FAILURE }
    | { type: typeof AUTH_SET_TENANT, tenant: string }
    | { type: typeof AUTH_SET_API_TOKEN, apiToken: string }
    | { type: typeof AUTH_SET_REFRESH_TOKEN, refreshToken?: string }
    | { type: typeof AUTH_UNSET_TOKENS }
    | { type: typeof AUTH_FORGOT_PASSWORD_REQUEST | typeof AUTH_SUBMIT_PASSWORD_REQUEST }
    | { type: typeof AUTH_SUBMIT_PASSWORD_FAILURE | typeof AUTH_SUBMIT_PASSWORD_SUCCESS }
    | { type: typeof AUTH_FORGOT_PASSWORD_SUCCESS | typeof AUTH_FORGOT_PASSWORD_FAILURE }

const initialState: AuthenticationState = {
    loading: false,
    authenticateFailure: false,
    newPasswordSuccess: false,
    isAuthenticated: !! apiTokenStorage.get(),
    tenant: tenantStorage.get(),
    apiToken: apiTokenStorage.get(),
    apiRefreshToken: apiRefreshTokenStorage.get(),
    errors: [],
}

export const authenticationReducer = (state: AuthenticationState = initialState, action: AuthenticationAction): AuthenticationState => {
    switch (action.type) {
        case AUTH_AUTHENTICATE_USER_REQUEST:
            return {
                ...state, loading: true, authenticateFailure: false, errors: [],
            }
        case AUTH_AUTHENTICATE_USER_SUCCESS:
            apiTokenStorage.set(action.apiToken)
            apiRefreshTokenStorage.set(action.refreshToken)
            authenticatedStorage.set(true)

            return {
                ...state, isAuthenticated: true, loading: false, apiToken: action.apiToken, errors: [],
            }
        case AUTH_AUTHENTICATE_USER_FAILURE:
            return {
                ...state,
                isAuthenticated: false,
                authenticateFailure: true,
                loading: false,
            }
        case AUTH_SET_TENANT:
            tenantStorage.set(action.tenant)
            return {
                ...state, tenant: action.tenant,
            }
        case AUTH_SET_API_TOKEN:
            apiTokenStorage.set(action.apiToken)
            return {
                ...state, apiToken: action.apiToken,
            }
        case AUTH_SET_REFRESH_TOKEN:
            // Without a refresh token the stored one is removed, so it can't be combined with a new api token
            if (action.refreshToken) {
                apiRefreshTokenStorage.set(action.refreshToken)
            } else {
                apiRefreshTokenStorage.remove()
            }

            return {
                ...state, apiRefreshToken: action.refreshToken ?? null,
            }
        case AUTH_UNSET_TOKENS:
            apiRefreshTokenStorage.remove()
            apiTokenStorage.remove()
            tenantStorage.remove()

            return {
                ...state,
                isAuthenticated: false,
                apiToken: null,
                apiRefreshToken: null,
                tenant: null,
            }
        case AUTH_FORGOT_PASSWORD_REQUEST:
        case AUTH_SUBMIT_PASSWORD_REQUEST:
            return { ...state, loading: true }
        case AUTH_SUBMIT_PASSWORD_FAILURE:
        case AUTH_SUBMIT_PASSWORD_SUCCESS:
            return { ...state, loading: false, newPasswordSuccess: true }
        case AUTH_FORGOT_PASSWORD_SUCCESS:
        case AUTH_FORGOT_PASSWORD_FAILURE:
            return { ...state, loading: false }
        default:
            return state
    }
}
